import { useState, useEffect } from 'react';
import { CustomerNav } from '../components/CustomerNav';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import axios from 'axios';
import { CalendarDays, Clock, MapPin, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyBookings() {
  const { token } = useAuth();
  const { t, isRTL } = useLanguage();
  const { formatPrice } = useCurrency();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const statusColors = {
    pending: 'bg-golden-hour/20 text-golden-hour border-golden-hour/30',
    confirmed: 'bg-sage/20 text-sage border-sage/30',
    completed: 'bg-deep-charcoal/10 text-deep-charcoal border-deep-charcoal/20',
    cancelled: 'bg-red-100 text-red-600 border-red-200'
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t('pending'),
      confirmed: t('confirmed'),
      completed: t('completed'),
      cancelled: t('cancelled')
    };
    return labels[status] || status;
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-warm-sand ${isRTL ? 'rtl' : 'ltr'}`}>
      <CustomerNav />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h1 className="font-heading text-4xl font-bold text-deep-charcoal mb-2">
              {t('myBookings')}
            </h1>
            <p className="font-body text-deep-charcoal/60">
              {t('viewManageBookings')}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terracotta"></div>
            </div>
          ) : bookings.length === 0 ? (
            <Card className="bg-white rounded-2xl shadow-soft border-0">
              <CardContent className="p-12 text-center">
                <Sparkles className="w-16 h-16 text-deep-charcoal/20 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-deep-charcoal mb-2">
                  {t('noBookingsYet')}
                </h3>
                <p className="font-body text-deep-charcoal/60">
                  {t('startBooking')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking, index) => (
                <Card 
                  key={booking.booking_id} 
                  className="bg-white rounded-2xl shadow-soft border-0 overflow-hidden animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`booking-card-${booking.booking_id}`}
                >
                  <CardContent className="p-6">
                    <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                      <div className="space-y-3">
                        <div className={`flex items-center gap-3 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <h3 className="font-heading text-xl font-semibold text-deep-charcoal">
                            {booking.service_name}
                          </h3>
                          <Badge className={`${statusColors[booking.status]} border capitalize`}>
                            {getStatusLabel(booking.status)}
                          </Badge>
                        </div>
                        
                        <div className={`flex flex-wrap gap-4 font-body text-sm text-deep-charcoal/70 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <MapPin className={`w-4 h-4 text-terracotta ${isRTL ? 'ml-1.5' : 'mr-1.5'}`} />
                            {booking.branch_name}
                          </div>
                          <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <CalendarDays className={`w-4 h-4 text-terracotta ${isRTL ? 'ml-1.5' : 'mr-1.5'}`} />
                            {booking.booking_date}
                          </div>
                          <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Clock className={`w-4 h-4 text-terracotta ${isRTL ? 'ml-1.5' : 'mr-1.5'}`} />
                            {booking.booking_time}
                          </div>
                        </div>

                        {booking.special_requests && (
                          <p className="font-body text-sm text-deep-charcoal/60 italic">
                            "{booking.special_requests}"
                          </p>
                        )}
                      </div>

                      <div className={`${isRTL ? 'text-left' : 'text-right'}`}>
                        <p className="font-body text-xs text-deep-charcoal/50">
                          {t('bookingId')}
                        </p>
                        <p className="font-mono text-sm text-deep-charcoal">
                          {booking.booking_id}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
